import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'
import { useRestaurant } from '@/context/RestaurantContext'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { DateRangeModal } from '@/components/ui/DateRangeModal'
import { MobileFloatingFilter } from '@/components/ui/MobileFloatingFilter'
import { AlertTriangle, ArrowDown, ArrowUp, ChevronLeft, ChevronRight, Download, Loader2, RefreshCw } from 'lucide-react'
import { ApiService } from '@/services/api'
import { parseDate, createDateRange } from '@/lib/dateUtils'
import { useBusinessDayDefaultRange } from '@/hooks/useBusinessDayDefaultRange'

/**
 * Shared building blocks for the owner-app reports (menu performance, GST, Z-report ...):
 * data loading with the date range, the page header, stat tiles and a sortable, pageable,
 * CSV-exportable table, so each report screen only describes its own numbers.
 */

export function useReport<T>(report: string, opts: { dateBound?: boolean } = {}) {
  const dateBound = opts.dateBound !== false
  const { currentRestaurant } = useRestaurant()
  const [dateRange, setDateRange] = useBusinessDayDefaultRange()
  const [data, setData] = useState<T | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    if (!currentRestaurant?._id) return
    setIsLoading(true)
    setError(null)
    try {
      const response = await ApiService.getReport<T>(currentRestaurant._id, report, dateBound ? dateRange : undefined)
      if (response.success) setData(response.data)
      else setError(response.message || 'Failed to load report')
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to load report')
    } finally {
      setIsLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentRestaurant?._id, report, dateBound, dateRange])

  useEffect(() => {
    load()
  }, [load])

  const onDateChange = (field: 'from' | 'to', value: string) => {
    const d = parseDate(value)
    setDateRange(prev => (field === 'from' ? createDateRange(d, prev.to) : createDateRange(prev.from, d)))
  }

  return { data, isLoading, error, load, dateRange, onDateChange, dateBound }
}

interface PageProps {
  eyebrow: string
  title: string
  subtitle?: string
  report: ReturnType<typeof useReport<any>>
  /** Called by the Export button; omit to hide it. */
  onExport?: () => void
  children: ReactNode
}

export function ReportPage({ eyebrow, title, subtitle, report, onExport, children }: PageProps) {
  const { isLoading, error, load, dateRange, onDateChange, dateBound, data } = report
  const heading = `${title} Date Range`
  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-[11px] font-medium uppercase tracking-widest text-muted-foreground">{eyebrow}</div>
          <h1 className="text-xl font-bold sm:text-2xl">{title}</h1>
          {subtitle && <p className="mt-0.5 text-sm text-muted-foreground">{subtitle}</p>}
        </div>
        <div className="flex items-center gap-2">
          {dateBound && (
            <div className="hidden md:block">
              <DateRangeModal
                dateRange={dateRange}
                onDateChange={onDateChange}
                onApply={load}
                title={heading}
                description={`Select the date range for ${title.toLowerCase()}`}
              />
            </div>
          )}
          {onExport && (
            <Button variant="outline" onClick={onExport} disabled={!data}>
              <Download className="mr-2 h-4 w-4" />
              Export
            </Button>
          )}
          <Button variant="outline" size="icon" onClick={load} disabled={isLoading} aria-label="Refresh">
            <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {dateBound && (
        <MobileFloatingFilter
          dateRange={dateRange}
          onDateChange={onDateChange}
          onApply={load}
          title={heading}
          description={`Select the date range for ${title.toLowerCase()}`}
        />
      )}

      {isLoading && !data ? (
        <div className="flex items-center justify-center gap-2 py-16 text-muted-foreground">
          <Loader2 className="h-6 w-6 animate-spin" /> Loading...
        </div>
      ) : error ? (
        <div className="flex flex-col items-center justify-center space-y-4 py-16">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10">
            <AlertTriangle className="h-6 w-6 text-destructive" />
          </div>
          <p className="text-sm text-muted-foreground">{error}</p>
          <Button variant="outline" onClick={load}>
            <RefreshCw className="mr-2 h-4 w-4" />
            Retry
          </Button>
        </div>
      ) : data ? (
        <div className={`space-y-5 ${isLoading ? 'opacity-60 transition-opacity' : ''}`}>{children}</div>
      ) : null}
    </div>
  )
}

export type Tone = 'good' | 'bad' | 'warn' | 'muted'

export const toneClass = (tone?: Tone) =>
  tone === 'good'
    ? 'text-green-600 dark:text-green-400'
    : tone === 'bad'
      ? 'text-red-500 dark:text-red-400'
      : tone === 'warn'
        ? 'text-amber-600 dark:text-amber-400'
        : tone === 'muted'
          ? 'text-muted-foreground'
          : ''

export interface StatDef {
  label: string
  value: ReactNode
  sub?: string
  tone?: Tone
  highlight?: 'warn'
}

export function StatGrid({ stats }: { stats: StatDef[] }) {
  return (
    <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
      {stats.map(s => (
        <Card key={s.label} className={s.highlight === 'warn' ? 'border-amber-500/40 bg-amber-500/5' : ''}>
          <CardContent className="p-4 sm:p-5">
            <div className={`text-xs ${s.highlight === 'warn' ? 'text-amber-600 dark:text-amber-400' : 'text-muted-foreground'}`}>{s.label}</div>
            <div className={`mt-1.5 truncate text-2xl font-bold sm:text-3xl ${toneClass(s.tone)}`}>{s.value}</div>
            {s.sub && <div className="mt-1 text-xs text-muted-foreground">{s.sub}</div>}
          </CardContent>
        </Card>
      ))}
    </div>
  )
}

export interface Column<R> {
  key: string
  header: string
  align?: 'left' | 'right'
  /** Cell content; defaults to the row's value at `key`. */
  render?: (row: R) => ReactNode
  /** Value used for sorting and CSV; defaults to the row's value at `key`. */
  value?: (row: R) => string | number | null | undefined
  sortable?: boolean
  mono?: boolean
}

export function exportCsv<R>(filename: string, columns: Column<R>[], rows: R[]) {
  const esc = (v: unknown) => `"${String(v ?? '').replace(/"/g, '""')}"`
  const cell = (c: Column<R>, r: R) => (c.value ? c.value(r) : (r as any)[c.key])
  const lines = [columns.map(c => esc(c.header)).join(',')]
  for (const r of rows) lines.push(columns.map(c => esc(cell(c, r))).join(','))
  const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `${filename}-${new Date().toISOString().slice(0, 10)}.csv`
  a.click()
  URL.revokeObjectURL(url)
}

interface TableProps<R> {
  title?: string
  hint?: string
  columns: Column<R>[]
  rows: R[]
  rowKey: (row: R, index: number) => string
  pageSize?: number
  empty?: string
  /** Initial sort column key, descending. */
  defaultSort?: string
  /** Adds an Export button to the card header. */
  csvName?: string
  /** Extra classes for a row, e.g. to highlight it. */
  rowClass?: (row: R) => string
  /** Optional totals row under the body. */
  footer?: ReactNode
}

export function DataTable<R>({ title, hint, columns, rows, rowKey, pageSize = 15, empty = 'Nothing to show for this period.', defaultSort, csvName, rowClass, footer }: TableProps<R>) {
  const [sortKey, setSortKey] = useState<string | null>(defaultSort ?? null)
  const [desc, setDesc] = useState(true)
  const [page, setPage] = useState(1)

  const sorted = useMemo(() => {
    const col = columns.find(c => c.key === sortKey)
    if (!col) return rows
    const get = (r: R) => (col.value ? col.value(r) : (r as any)[col.key])
    return [...rows].sort((a, b) => {
      const av = get(a)
      const bv = get(b)
      const cmp = typeof av === 'number' && typeof bv === 'number' ? av - bv : String(av ?? '').localeCompare(String(bv ?? ''))
      return desc ? -cmp : cmp
    })
  }, [rows, columns, sortKey, desc])

  useEffect(() => setPage(1), [rows])

  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize))
  const pageRows = sorted.slice((page - 1) * pageSize, page * pageSize)

  const onSort = (c: Column<R>) => {
    if (c.sortable === false) return
    if (sortKey === c.key) setDesc(d => !d)
    else {
      setSortKey(c.key)
      setDesc(c.align === 'right')
    }
  }

  return (
    <Card className="overflow-hidden">
      {(title || csvName) && (
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
          <div>
            {title && <CardTitle className="text-base">{title}</CardTitle>}
            {hint && <p className="mt-0.5 text-xs text-muted-foreground">{hint}</p>}
          </div>
          {csvName && rows.length > 0 && (
            <Button variant="ghost" size="sm" onClick={() => exportCsv(csvName, columns, sorted)}>
              <Download className="mr-1.5 h-3.5 w-3.5" />
              CSV
            </Button>
          )}
        </CardHeader>
      )}
      {rows.length === 0 ? (
        <CardContent className="py-10 text-center text-sm text-muted-foreground">{empty}</CardContent>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[560px] text-sm">
              <thead>
                <tr className="border-t text-[11px] uppercase tracking-wider text-muted-foreground">
                  {columns.map(c => (
                    <th
                      key={c.key}
                      className={`px-4 py-3 font-medium ${c.align === 'right' ? 'text-right' : 'text-left'} ${c.sortable === false ? '' : 'cursor-pointer select-none'}`}
                      onClick={() => onSort(c)}
                    >
                      <span className="inline-flex items-center gap-1">
                        {c.header}
                        {sortKey === c.key && (desc ? <ArrowDown className="h-3 w-3" /> : <ArrowUp className="h-3 w-3" />)}
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {pageRows.map((r, i) => (
                  <tr key={rowKey(r, i)} className={`border-t ${rowClass ? rowClass(r) : ''}`}>
                    {columns.map(c => (
                      <td
                        key={c.key}
                        className={`px-4 py-3 ${c.align === 'right' ? 'whitespace-nowrap text-right tabular-nums' : ''} ${c.mono ? 'font-mono text-xs' : ''}`}
                      >
                        {c.render ? c.render(r) : String((r as any)[c.key] ?? '—')}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
              {footer}
            </table>
          </div>
          {totalPages > 1 && (
            <div className="flex items-center justify-between border-t px-4 py-3">
              <span className="text-xs text-muted-foreground">
                Page {page} of {totalPages} · {rows.length} rows
              </span>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </Card>
  )
}

/** Small coloured pill, used for classes, statuses and ageing. */
export function Pill({ tone, children }: { tone?: Tone; children: ReactNode }) {
  const cls =
    tone === 'good'
      ? 'border-green-500/30 bg-green-500/10 text-green-600 dark:text-green-400'
      : tone === 'bad'
        ? 'border-red-500/30 bg-red-500/10 text-red-500 dark:text-red-400'
        : tone === 'warn'
          ? 'border-amber-500/30 bg-amber-500/10 text-amber-600 dark:text-amber-400'
          : 'border-border bg-muted text-muted-foreground'
  return <span className={`inline-flex items-center rounded-md border px-2 py-0.5 text-xs font-medium ${cls}`}>{children}</span>
}

export const dateTimeLabel = (iso?: string | null) => {
  if (!iso) return '—'
  const d = new Date(iso)
  if (isNaN(d.getTime())) return '—'
  return `${d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}, ${d
    .toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true })
    .toLowerCase()}`
}

export const dayLabel = (day: string) => {
  const d = new Date(`${day}T00:00:00`)
  return isNaN(d.getTime()) ? day : d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
}

export const titleCase = (s: string) => s.replace(/\b\w/g, c => c.toUpperCase())
